import { CORES_AUTOMOTIVAS } from '../../dominio/didatico/tokens'
import { ResgateTabletop } from './ResgateTabletop'
import { OnibusTabletop } from './OnibusTabletop'
import { Edges, TransformControls } from '@react-three/drei'
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import * as THREE from 'three'
import type { Group } from 'three'
import {
  ancoraMaisProxima,
  alvoAmassadoNoPonto,
  ancorasLivresDoVeiculo,
  avaliarEstabilizacaoDaCena,
  colocacaoLocalDaVitimaNoVeiculo,
  encaixarPosicaoXZ,
  estadoVisualDeformacoesTabletop,
  pontoMundoNoVeiculo,
  podeApoiarEmVeiculo,
  type AvaliacaoEstabilizacaoPorVeiculo,
  type EstadoColocacao,
  type ObjetoTabletop,
  type PrioridadeTriagem,
  type PosturaVitimaSolo,
  type TipoObjetoTabletop,
  type TransformacaoTabletop,
} from '../../dominio/tabletop'
import { buscarIntersecaoSuperficie, calcularRotacaoSuperficie } from './superficieApoio'
import { ponteiroArrastou, type PontoDeTela } from '../gestoPonteiro'
import type { AparenciaRisco } from '../../dominio/tabletop/atendimento/riscos'
import type { MaterialAbsorvente } from '../../dominio/tabletop/tipos'
import {
  atualizarSuspensao,
  ESTADO_SUSPENSAO_INICIAL,
  type EstadoSuspensao,
  type ForcaPerturbacao,
} from '../../dominio/didatico/suspensaoVeiculo'
import { HATCH_COMPACTO, SEDAN_MEDIO, perfilVeiculoPorId, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import type { MapaEstados } from '../../dominio/didatico/estadoPecas'
import type { EstadoManejoVidro, IdVidro } from '../../dominio/didatico/manejoVidros'
import { ADULTO_MASCULINO, perfilVitimaPorId } from '../../dominio/didatico/perfisVitima'
import { POSE_ALINHADA, POSE_SENTADA } from '../../dominio/didatico/rigVitima'
import { VeiculoAlvoCompleto, VeiculoDidatico } from '../veiculo/VeiculoDidatico'
import type { EstadoDeformacaoVeiculo } from '../../dominio/didatico/campoDeformacao'
import { VitimaProcedural } from '../vitima/VitimaProcedural'
import { DetritosVidroPersistentes3D } from '../vidros/DetritosVidroPersistentes3D'
import { StepChock3D, CunhaResgate3D, BlocoCribbing3D } from '../estabilizacao/EquipamentosCribbing'
import { VStrut3D } from '../estabilizacao/VStrut3D'
import { PistaAsfalto3D } from './PistaAsfalto3D'
import { DioramaTabletop } from './DioramaTabletop'
import { ArvoreMaquete, CaminhaoCarga3D, DefensaMaquete, Moto3D } from './objetosMaquete'
import { VidroDanificado } from './VidroDanificado'
import { CameraDaMesa } from './CameraDaMesa'
import {
  CaminhaoBombeirosTabletop,
  BombeiroTabletop,
  BombeiroMedicoTabletop,
  AreaUsaTabletop,
  ManchaSangueTabletop,
  ExtintorPqsTabletop,
  MochilaTraumaTabletop,
  LonaFerramentasTabletop,
  PosteTabletop,
} from './ObjetosTaticosTabletop'
import { FaiscasChoqueEletrico3D, Fogo3D } from '../componentes/FogoEFaiscasChoque3D'
import { VITIMAS_POR_ID } from '../../dominio/tabletop/atendimento/vitimasCadastradas'
import { ProvedorMaquete, type ValorMaquete } from '../componentes/contextoMaquete'

function resolverRegiaoHemorragiaVitima(objeto: ObjetoTabletop) {
  if (objeto.vitima?.regiaoHemorragia) return objeto.vitima.regiaoHemorragia
  if (objeto.vitima?.fichaAtendimentoId) {
    const ficha = VITIMAS_POR_ID[objeto.vitima.fichaAtendimentoId]
    const hemo = ficha?.achadosIniciais?.x_hemorragia_externa
    if (hemo?.estado === 'observado' && hemo.regiao) {
      return hemo.regiao
    }
    const problemaVisivel = ficha?.problemas?.find((p) => p.visivelNaCena && p.regiao)
    if (problemaVisivel?.regiao) {
      return problemaVisivel.regiao
    }
  }
  return undefined
}

import { regioesVidroDoPerfil } from '../vidros/regioesVidro'
import { PREFIXO_SELECIONAVEL } from '../camera/comandosCamera'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

export type FerramentaTabletop = 'translate' | 'rotate' | 'scale'

/** Cor do anel de triagem no chão, para a vítima selecionada ou não — mesma paleta do painel. */
const CORES_TRIAGEM_3D: Record<string, string> = {
  vermelho: '#ef4444',
  amarelo: '#f59e0b',
  verde: '#22c55e',
  preto: '#1f2937',
}

/** Mesma paleta de estado que a aba de Estabilização usa para um único veículo. */
const CORES_ESTADO_EQUIPAMENTO: Record<EstadoColocacao, string> = {
  correta: '#10b981',
  proxima: '#f59e0b',
  fora: '#ef4444',
  naoPrevista: '#ef4444',
}

/**
 * Camadas do carro selecionado no Table top: gaiola, zonas de absorção, SRS
 * e cinto visíveis, carroceria translúcida — o mesmo espírito do preset
 * "Estrutura" da aba de estrutura veicular, só que aplicado por carro.
 */
const CAMADAS_ESTRUTURA_TABLETOP: ValorMaquete['camadas'] = {
  estrutura: true,
  zonaDeformacao: true,
  srs: true,
  retencao: true,
  carroceria: true,
  habitaculo: true,
  // o Table top pode ter vários veículos na mesma cena e a aula ali é sobre
  // posição e estabilização, não sobre mecânica
  mecanica: false,
  energia: false,
  vitima: true,
}
const OPACIDADE_CARROCERIA_TABLETOP = 0.16
/**
 * Veículo com porta aberta ou corte, mas não selecionado: aparece como carro de
 * verdade — carroceria opaca na cor dele, sem a zona de absorção didática. O
 * raio-X da estrutura fica para quando ele é selecionado para editar peças.
 */
const CAMADAS_VEICULO_REAL_TABLETOP: ValorMaquete['camadas'] = {
  ...CAMADAS_ESTRUTURA_TABLETOP,
  zonaDeformacao: false,
  // módulos de airbag ficam atrás do acabamento num carro de verdade
  srs: false,
}
const NOOP_SELECIONAR = () => {}
const pararPropagacao = (evento: ThreeEvent<MouseEvent>) => evento.stopPropagation()
/**
 * Há uma peça esperando o toque no chão. Nesse caso o clique em cima de um
 * objeto (ex.: o calço vai sob a soleira, que na tela fica "em cima" do carro)
 * precisa atravessar até o plano do chão — senão a peça nunca nasce ali.
 */
const ContextoPosicionando = createContext(false)
const LISTA_VAZIA: readonly string[] = []

export interface AtendimentoCena3D {
  readonly modoExibicao: 'aluno' | 'instrutor'
  readonly triagensDeclaradas: Readonly<Record<string, PrioridadeTriagem>>
  readonly colaresColocados: Readonly<Record<string, boolean>>
  /** sangramento visível por vítima enquanto o problema da ficha não é controlado */
  readonly sangramentos?: Readonly<Record<string, 'ativo' | 'parcial'>>
  /** aparência dos riscos na sessão: varrido, coberto, absorvente, área isolada, energizado */
  readonly riscos?: Readonly<Record<string, AparenciaRisco>>
  readonly idVitimaFoco?: string | null
  readonly regiaoDestaque?: string | null
}

/**
 * Pedido explícito de "testar estabilidade" num veículo. Cada pedido novo
 * leva um `serial` diferente, para o mesmo carro poder ser testado de novo.
 */
export interface PedidoTesteBalanco {
  readonly idVeiculo: string
  readonly serial: number
}

/** Empurrão aplicado pelo teste de estabilidade: para baixo, rolando e arfando ao mesmo tempo. */
const FORCA_TESTE_BALANCO: ForcaPerturbacao = { forcaY: -14, torqueRoll: 10, torquePitch: 8, forcaZ: 3 }

export interface PropsCenaTabletop {
  objetos: readonly ObjetoTabletop[]
  idSelecionado: string | null
  ferramenta: FerramentaTabletop
  aoSelecionar: (id: string | null) => void
  aoTransformar: (id: string, transformacao: TransformacaoTabletop) => void
  aoAlterarManipulacao?: (ativa: boolean) => void
  aoCancelarManipulacao?: () => void
  encaixeAtivo?: boolean
  aoSelecionarPeca?: (idVeiculo: string, idPeca: string | null) => void
  atendimento?: AtendimentoCena3D
  /** Modo Atender: a cena fica travada (sem gizmo), só a seleção continua ativa para focar uma vítima. */
  somenteLeitura?: boolean
  /** Peça da bandeja aguardando um toque no chão para nascer ali (ver B2, `useTabletop.iniciarPosicionamento`). */
  tipoParaPosicionar?: TipoObjetoTabletop | null
  escolhendoAlvo?: boolean
  aoPosicionarNovaPeca?: (
    tipo: TipoObjetoTabletop,
    x: number,
    z: number,
    y?: number,
    rotacao?: readonly [number, number, number],
  ) => void
  /** B4: soltar uma vítima do chão perto de uma âncora livre já a senta ali, em vez de só reposicionar no chão. */
  aoVincularVitima?: (idVitima: string, idVeiculo: string, idAncora: string) => void
  /** B4 (volta pro chão): desvincula uma vítima já sentada, colocando-a exatamente na posição de mundo em que estava. */
  aoDesvincularVitima?: (idVitima: string) => void
  /** Selecionar um veículo não o balança mais; o balanço só vem deste pedido explícito. */
  pedidoTesteBalanco?: PedidoTesteBalanco | null
  pincel?: {
    ativo: boolean
    larguraM: number
    intrusaoM: number
    aoAplicar: (idVeiculo: string, ponto: Readonly<{ x: number; y: number; z: number }>) => void
  }
  /** Exibe a pista de asfalto com faixas viárias, acostamento e marcas de frenagem (padrão true). */
  exibirAsfalto?: boolean
  /** Trava veículos e obstáculos no cenário, permitindo movimentar apenas equipamentos/ferramentas. */
  cenarioTravado?: boolean
  /**
   * Com `somenteLeitura` (Atender), ainda permite reposicionar por arraste o
   * que não é vítima — mover vítima na sessão é operação de atendimento.
   */
  arrastoNaSessao?: boolean
}

const TIPOS_EQUIPAMENTO_TABLETOP: readonly TipoObjetoTabletop[] = [
  'calco', 'cunha', 'vstrut', 'fitaCatraca', 'bloco', 'cobertor', 'lagrima', 'prancha',
]
const ehEquipamentoTabletop = (objeto: ObjetoTabletop) =>
  Boolean(objeto.equipamento || TIPOS_EQUIPAMENTO_TABLETOP.includes(objeto.tipo))

export function CenaTabletop({
  objetos,
  idSelecionado,
  ferramenta,
  aoSelecionar,
  aoTransformar,
  aoAlterarManipulacao,
  aoCancelarManipulacao,
  encaixeAtivo = false,
  aoSelecionarPeca,
  atendimento,
  somenteLeitura,
  tipoParaPosicionar,
  escolhendoAlvo = false,
  aoPosicionarNovaPeca,
  aoVincularVitima,
  aoDesvincularVitima,
  pedidoTesteBalanco,
  pincel,
  exibirAsfalto = true,
  cenarioTravado = false,
  arrastoNaSessao = false,
}: PropsCenaTabletop) {

  // vítimas vinculadas a um veículo são renderizadas DENTRO do grupo do carro
  // (para herdar rolagem/guinada automaticamente) e saem da lista de objetos
  // independentes, que continua tendo gizmo próprio
  const vitimasPorVeiculo = useMemo(() => {
    const mapa = new Map<string, ObjetoTabletop[]>()
    for (const objeto of objetos) {
      const local = objeto.vitima?.local
      if (local?.tipo !== 'veiculo') continue
      const lista = mapa.get(local.idVeiculo) ?? []
      lista.push(objeto)
      mapa.set(local.idVeiculo, lista)
    }
    return mapa
  }, [objetos])

  const idsVinculados = useMemo(
    () => new Set(objetos.filter((o) => o.vitima?.local.tipo === 'veiculo').map((o) => o.id)),
    [objetos],
  )

  const { estadoPorEquipamento, avaliacoesPorVeiculo } = useMemo(() => {
    const estados = new Map<string, EstadoColocacao>()
    const porVeiculo = new Map<string, AvaliacaoEstabilizacaoPorVeiculo>()
    for (const avaliacao of avaliarEstabilizacaoDaCena(objetos)) {
      porVeiculo.set(avaliacao.idVeiculo, avaliacao)
      for (const [id, estado] of avaliacao.estadoPorEquipamento) estados.set(id, estado)
    }
    return { estadoPorEquipamento: estados, avaliacoesPorVeiculo: porVeiculo }
  }, [objetos])

  // A peça só se move depois de um arraste intencional. No touch, pressionar
  // por 350 ms ativa o gesto; mover antes disso continua sendo órbita da câmera.
  const [arrastandoObjetoId, setArrastandoObjetoId] = useState<string | null>(null)
  const [ancoraDestacada, setAncoraDestacada] = useState<{ idVeiculo: string; idAncora: string } | null>(null)
  const ancoraDestacadaRef = useRef<{ idVeiculo: string; idAncora: string } | null>(null)
  const gestoRef = useRef<{
    id: string
    pointerId: number
    inicio: PontoDeTela
    soloInicial: THREE.Vector3
    origem: TransformacaoTabletop['posicao'] | null
    temporizador: number | null
    ativo: boolean
    manipulando: boolean
    moveu: boolean
    desvinculou: boolean
  } | null>(null)

  let camera: THREE.Camera | undefined
  let raycaster: THREE.Raycaster | undefined
  let canvas: HTMLCanvasElement | undefined
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const three = useThree()
    camera = three.camera
    raycaster = three.raycaster
    canvas = three.gl.domElement
  } catch {
    // fora do <Canvas> (ex.: testes de unidade)
  }
  const planoChao = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), [])
  const veiculos3DRef = useRef<Map<string, THREE.Group>>(new Map())
  const registrarVeiculo3D = useCallback((id: string, grp: THREE.Group | null) => {
    if (grp) veiculos3DRef.current.set(id, grp)
    else veiculos3DRef.current.delete(id)
  }, [])
  const pontoNoSolo = (x: number, y: number): THREE.Vector3 | null => {
    if (!canvas || !camera || !raycaster) return null
    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null
    raycaster.setFromCamera(
      new THREE.Vector2(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1),
      camera,
    )
    return raycaster.ray.intersectPlane(planoChao, new THREE.Vector3())
  }

  const objetoArrastado = arrastandoObjetoId ? objetos.find((o) => o.id === arrastandoObjetoId) ?? null : null
  const vitimaArrastadaPodeSentar =
    objetoArrastado?.tipo === 'vitima' && objetoArrastado.vitima?.local.tipo !== 'veiculo'

  const ancorasLivresNaCena = useMemo(() => {
    if (!vitimaArrastadaPodeSentar) return []
    const marcadores: Array<{ idVeiculo: string; idAncora: string; posicao: readonly [number, number, number] }> = []
    for (const veiculo of objetos) {
      if (!veiculo.veiculo) continue
      for (const ancora of ancorasLivresDoVeiculo(veiculo, objetos)) {
        const mundo = pontoMundoNoVeiculo(ancora.posicao, veiculo)
        marcadores.push({ idVeiculo: veiculo.id, idAncora: ancora.id, posicao: [mundo.x, mundo.y, mundo.z] })
      }
    }
    return marcadores
  }, [vitimaArrastadaPodeSentar, objetos])

  const iniciarCandidatoArrasto = (id: string, evento: PointerEvent) => {
    if (gestoRef.current || escolhendoAlvo || ferramenta !== 'translate') return
    const objeto = objetos.find((item) => item.id === id)
    if (!objeto || objeto.bloqueado || (cenarioTravado && !ehEquipamentoTabletop(objeto))) return
    if (somenteLeitura && !(arrastoNaSessao && objeto.tipo !== 'vitima')) return
    const soloInicial = pontoNoSolo(evento.clientX, evento.clientY)
    if (!soloInicial) return
    const toque = evento.pointerType === 'touch'
    const gesto = {
      id, pointerId: evento.pointerId,
      inicio: { x: evento.clientX, y: evento.clientY },
      soloInicial,
      origem: objeto.vitima?.local.tipo === 'veiculo' ? null : objeto.transformacao.posicao,
      temporizador: null as number | null,
      ativo: false, manipulando: !toque, moveu: false, desvinculou: false,
    }
    gestoRef.current = gesto
    if (toque) {
      gesto.temporizador = window.setTimeout(() => {
        if (gestoRef.current !== gesto) return
        gesto.ativo = true
        gesto.manipulando = true
        aoAlterarManipulacao?.(true)
        setArrastandoObjetoId(id)
      }, 350)
    } else {
      // Suspende a órbita desde o pressionamento no mouse; se for só clique,
      // finalizarManipulacao não registra nenhum passo no histórico.
      aoAlterarManipulacao?.(true)
    }
  }

  useEffect(() => {
    const limparGesto = (cancelado: boolean) => {
      const gesto = gestoRef.current
      if (!gesto) return
      if (gesto.temporizador !== null) window.clearTimeout(gesto.temporizador)
      gestoRef.current = null
      if (!cancelado && gesto.moveu && ancoraDestacadaRef.current) {
        const ancora = ancoraDestacadaRef.current
        aoVincularVitima?.(gesto.id, ancora.idVeiculo, ancora.idAncora)
      }
      ancoraDestacadaRef.current = null
      setAncoraDestacada(null)
      setArrastandoObjetoId(null)
      if (gesto.manipulando) {
        if (cancelado && aoCancelarManipulacao) aoCancelarManipulacao()
        else aoAlterarManipulacao?.(false)
      }
    }

    const aoMover = (evento: PointerEvent) => {
      const gesto = gestoRef.current
      if (!gesto || gesto.pointerId !== evento.pointerId) return
      const deslocou = ponteiroArrastou(gesto.inicio, { x: evento.clientX, y: evento.clientY })
      if (!gesto.ativo) {
        if (!deslocou) return
        if (gesto.temporizador !== null) {
          // Touch movido antes da pressão prolongada: liberar para orbitar.
          limparGesto(true)
          return
        }
        gesto.ativo = true
        setArrastandoObjetoId(gesto.id)
      }
      if (!deslocou) return
      const objeto = objetos.find((item) => item.id === gesto.id)
      if (!objeto) return
      if (objeto.vitima?.local.tipo === 'veiculo') {
        if (!gesto.desvinculou) {
          gesto.desvinculou = true
          aoDesvincularVitima?.(gesto.id)
        }
        return
      }

      if (!canvas || !camera || !raycaster) return
      const rect = canvas.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      raycaster.setFromCamera(
        new THREE.Vector2(((evento.clientX - rect.left) / rect.width) * 2 - 1, -((evento.clientY - rect.top) / rect.height) * 2 + 1),
        camera,
      )

      let finalX: number
      let finalY: number
      let finalZ: number
      let novaRotacao = objeto.transformacao.rotacao

      if (podeApoiarEmVeiculo(objeto.tipo)) {
        const intersecao = buscarIntersecaoSuperficie(
          raycaster,
          veiculos3DRef.current,
          planoChao,
          objeto.tipo,
        )
        if (!intersecao) return
        const [x, z] = encaixeAtivo
          ? encaixarPosicaoXZ(intersecao.ponto.x, intersecao.ponto.z)
          : [intersecao.ponto.x, intersecao.ponto.z]
        finalX = x
        finalY = intersecao.ponto.y
        finalZ = z
        novaRotacao = calcularRotacaoSuperficie(
          objeto.tipo,
          intersecao.normal,
          objeto.transformacao.rotacao[1],
        )

        if (objeto.tipo === 'vitima') {
          const achado = ancoraMaisProxima({ x: finalX, z: finalZ }, objetos)
          const proximo = achado ? { idVeiculo: achado.idVeiculo, idAncora: achado.idAncora } : null
          ancoraDestacadaRef.current = proximo
          setAncoraDestacada((atual) =>
            atual?.idVeiculo === proximo?.idVeiculo && atual?.idAncora === proximo?.idAncora ? atual : proximo,
          )
        }
      } else {
        const ponto = raycaster.ray.intersectPlane(planoChao, new THREE.Vector3())
        if (!ponto) return
        // Após desvincular uma vítima, a posição atual já é calculada em mundo.
        if (!gesto.origem) gesto.origem = objeto.transformacao.posicao
        const x = gesto.origem[0] + ponto.x - gesto.soloInicial.x
        const z = gesto.origem[2] + ponto.z - gesto.soloInicial.z
        const [encX, encZ] = encaixeAtivo ? encaixarPosicaoXZ(x, z) : [x, z]
        finalX = encX
        finalY = objeto.transformacao.posicao[1]
        finalZ = encZ
      }

      gesto.moveu = true
      aoTransformar(gesto.id, {
        ...objeto.transformacao,
        posicao: [finalX, finalY, finalZ],
        rotacao: novaRotacao,
      })
    }
    const aoSoltar = (evento: PointerEvent) => {
      if (gestoRef.current?.pointerId === evento.pointerId) limparGesto(false)
    }
    const aoCancelar = (evento: PointerEvent) => {
      if (gestoRef.current?.pointerId === evento.pointerId) limparGesto(true)
    }
    const aoPerderFoco = () => limparGesto(true)
    window.addEventListener('pointermove', aoMover)
    window.addEventListener('pointerup', aoSoltar)
    window.addEventListener('pointercancel', aoCancelar)
    window.addEventListener('blur', aoPerderFoco)
    return () => {
      window.removeEventListener('pointermove', aoMover)
      window.removeEventListener('pointerup', aoSoltar)
      window.removeEventListener('pointercancel', aoCancelar)
      window.removeEventListener('blur', aoPerderFoco)
    }
  }, [objetos, camera, raycaster, canvas, planoChao, encaixeAtivo, aoTransformar,
    aoAlterarManipulacao, aoCancelarManipulacao, aoVincularVitima, aoDesvincularVitima])

  const cancelarAoDesmontarRef = useRef(aoCancelarManipulacao)
  cancelarAoDesmontarRef.current = aoCancelarManipulacao
  useEffect(() => () => {
    const gesto = gestoRef.current
    if (gesto?.temporizador !== null && gesto?.temporizador !== undefined) window.clearTimeout(gesto.temporizador)
    if (gesto?.manipulando) cancelarAoDesmontarRef.current?.()
  }, [])

  // Tocar no chão cria uma nova peça vinda da bandeja, sem confundir órbita com clique.
  const pressaoRef = useRef<PontoDeTela | null>(null)
  useEffect(() => {
    const registrarPressao = (evento: PointerEvent) => {
      pressaoRef.current = { x: evento.clientX, y: evento.clientY }
    }
    window.addEventListener('pointerdown', registrarPressao)
    return () => window.removeEventListener('pointerdown', registrarPressao)
  }, [])

  // O toque que começou num objeto não desseleciona ao terminar. Em Montar,
  // o objeto recém-selecionado é remontado dentro do TransformControls; para
  // o R3F o clique já não "é" dele, e cai neste plano do chão — sem esta
  // marca, tocar num objeto o selecionava e o desselecionava no mesmo gesto.
  const pressaoEmObjetoRef = useRef(false)
  const selecionarPorToque = (id: string | null) => {
    pressaoEmObjetoRef.current = true
    aoSelecionar(id)
  }

  const aoTocarChao = (evento: ThreeEvent<MouseEvent>) => {
    if (escolhendoAlvo) return
    // órbita de câmera termina sobre este mesmo plano; sem a checagem de
    // arrasto, toda vez que a turma girasse a câmera nasceria uma peça nova
    if (ponteiroArrastou(pressaoRef.current, { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY })) return
    if (!tipoParaPosicionar) {
      if (idSelecionado && !pressaoEmObjetoRef.current) aoSelecionar(null)
      return
    }
    const [x, z] = encaixeAtivo
      ? encaixarPosicaoXZ(evento.point.x, evento.point.z)
      : [evento.point.x, evento.point.z]
    aoPosicionarNovaPeca?.(tipoParaPosicionar, x, z)
  }

  return (
    <ContextoPosicionando.Provider value={Boolean(tipoParaPosicionar)}>
    <group>
      {/* Pista de asfalto com faixas viárias, acostamento e marcas de frenagem */}
      {exibirAsfalto && <PistaAsfalto3D />}
      {exibirAsfalto && <DioramaTabletop />}
      <CameraDaMesa />

      {/* Plano invisível: recebe o toque de "colocar peça aqui" e o de
          desselecionar ao tocar fora de qualquer objeto. Maior que a grade
          visual para cobrir também uma câmera bem afastada. Mesmo truque de
          `CenaEstabilizacao` (opacidade 0 em vez de `visible=false`, que
          continuaria clicável mas sem o controle fino de profundidade). */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.001, 0]}
        onPointerDown={() => {
          pressaoEmObjetoRef.current = false
        }}
        onClick={aoTocarChao}
      >
        <planeGeometry args={[60, 60]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* B4: bancos livres acendem enquanto uma vítima do chão é arrastada;
          o mais próximo (dentro do raio de encaixe) fica mais forte/opaco. */}
      {ancorasLivresNaCena.map((marcador) => {
        const destacada =
          ancoraDestacada?.idVeiculo === marcador.idVeiculo && ancoraDestacada?.idAncora === marcador.idAncora
        return (
          <mesh
            key={`${marcador.idVeiculo}:${marcador.idAncora}`}
            position={[marcador.posicao[0], marcador.posicao[1] + 0.03, marcador.posicao[2]]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <ringGeometry args={destacada ? [0.28, 0.4, 32] : [0.32, 0.38, 32]} />
            <meshBasicMaterial
              color={destacada ? '#22c55e' : '#38bdf8'}
              transparent
              opacity={destacada ? 0.95 : 0.5}
              depthTest={false}
            />
          </mesh>
        )
      })}

      {/* Sangramento da vítima no chão ou lataria: fica no nível do mundo na cota da vítima */}
      {objetos.map((objeto) => {
        const sangramento = atendimento?.sangramentos?.[objeto.id]
        if (!sangramento || objeto.oculto || idsVinculados.has(objeto.id)) return null
        const [x, y, z] = objeto.transformacao.posicao
        const ySangue = Math.max(0.012, y + 0.005)
        return <Sangramento3D key={`sangue-${objeto.id}`} intensidade={sangramento} posicao={[x, ySangue, z]} />
      })}

      {objetos.map((objeto) => {
        if (objeto.oculto || idsVinculados.has(objeto.id)) return null
        return (
          <ObjetoEditavel
            key={objeto.id}
            objeto={objeto}
            selecionado={objeto.id === idSelecionado}
            ferramenta={ferramenta}
            vitimasVinculadas={objeto.veiculo ? vitimasPorVeiculo.get(objeto.id) ?? [] : []}
            idVitimaSelecionada={idSelecionado}
            corEquipamento={objeto.equipamento ? estadoPorEquipamento.get(objeto.id) : undefined}
            avaliacaoEstabilizacao={objeto.veiculo ? avaliacoesPorVeiculo.get(objeto.id) : undefined}
            pedidoTesteBalanco={pedidoTesteBalanco?.idVeiculo === objeto.id ? pedidoTesteBalanco : null}
            pincel={pincel}
            atendimento={atendimento}
            somenteLeitura={somenteLeitura || escolhendoAlvo}
            aoSelecionar={selecionarPorToque}
            aoTransformar={aoTransformar}
            aoAlterarManipulacao={aoAlterarManipulacao}
            aoIniciarArrastoObjeto={escolhendoAlvo || ferramenta !== 'translate' ? undefined : iniciarCandidatoArrasto}
            aoIniciarArrastoParaFora={escolhendoAlvo || ferramenta !== 'translate' ? undefined : iniciarCandidatoArrasto}
            aoSelecionarPeca={(idPeca) => aoSelecionarPeca?.(objeto.id, idPeca)}
            cenarioTravado={cenarioTravado}
            arrastoNaSessao={arrastoNaSessao}
            aoRegistrarVeiculo3D={objeto.veiculo ? registrarVeiculo3D : undefined}
            tipoParaPosicionar={tipoParaPosicionar}
            aoPosicionarNovaPeca={aoPosicionarNovaPeca}
            encaixeAtivo={encaixeAtivo}
            pressaoRef={pressaoRef}
          />
        )
      })}
    </group>
    </ContextoPosicionando.Provider>
  )
}

/**
 * Física de suspensão de UM veículo, imperativa (sem re-render do React a
 * cada quadro): o estado vive numa ref e o quadro só escreve direto na
 * transformação do grupo — com vários carros na cena isso evita recalcular
 * a árvore React 60x por segundo. A rigidez vem da avaliação de
 * estabilização DESTE carro: sem nada calçado, ele balança; travado (ou sem
 * avaliação — objeto sem bloco `veiculo`), fica parado.
 */
function useSuspensaoDoVeiculo(avaliacao: AvaliacaoEstabilizacaoPorVeiculo | undefined, ativa: boolean) {
  const grupoSuspensaoRef = useRef<Group>(null)
  const estadoRef = useRef<EstadoSuspensao>(ESTADO_SUSPENSAO_INICIAL)
  const perturbacaoRef = useRef<ForcaPerturbacao>({})

  useFrame((_, deltaBruto) => {
    if (!ativa) return
    const dt = Math.min(deltaBruto, 0.05)

    estadoRef.current = atualizarSuspensao(
      estadoRef.current,
      dt,
      avaliacao?.avaliacao.suspensaoAnulada ?? false,
      avaliacao?.avaliacao.rolamentoTravado ?? false,
      perturbacaoRef.current,
      undefined,
      avaliacao?.avaliacao.rigidezProgressiva,
    )
    // amortece o impulso da perturbação, como no loop de simulação da aba de Estabilização
    perturbacaoRef.current = {
      forcaY: (perturbacaoRef.current.forcaY ?? 0) * 0.82,
      torqueRoll: (perturbacaoRef.current.torqueRoll ?? 0) * 0.82,
      torquePitch: (perturbacaoRef.current.torquePitch ?? 0) * 0.82,
      forcaZ: (perturbacaoRef.current.forcaZ ?? 0) * 0.82,
    }

    const grupo = grupoSuspensaoRef.current
    if (grupo) {
      grupo.position.set(0, estadoRef.current.deslocamentoY, estadoRef.current.deslocamentoZ)
      grupo.rotation.set(estadoRef.current.anguloPitch, 0, estadoRef.current.anguloRoll)
    }
  })

  const perturbar = (forca: ForcaPerturbacao) => {
    perturbacaoRef.current = forca
  }

  return { grupoSuspensaoRef, perturbar }
}

/**
 * Só existe para objetos com bloco `veiculo` — é por isso que `useFrame` (via
 * `useSuspensaoDoVeiculo`) roda apenas para veículos de verdade, e não para
 * cada árvore, cone ou fragmento de vidro da cena. `perturbarRef` devolve o
 * "testar balanço" para `ObjetoEditavel`, que é quem recebe o clique.
 */
function ConteudoVeiculoSuspenso({
  objeto,
  corEquipamento,
  selecionado,
  vitimasVinculadas,
  idVitimaSelecionada,
  avaliacaoEstabilizacao,
  suspensaoAtiva,
  perturbarRef,
  atendimento,
  somenteLeitura,
  aoSelecionar,
  aoSelecionarPeca,
  aoIniciarArrastoParaFora,
  modoPincel,
}: {
  objeto: ObjetoTabletop
  corEquipamento: EstadoColocacao | undefined
  selecionado: boolean
  vitimasVinculadas: readonly ObjetoTabletop[]
  idVitimaSelecionada: string | null
  avaliacaoEstabilizacao: AvaliacaoEstabilizacaoPorVeiculo | undefined
  suspensaoAtiva: boolean
  perturbarRef: MutableRefObject<((forca: ForcaPerturbacao) => void) | null>
  atendimento?: AtendimentoCena3D
  somenteLeitura?: boolean
  aoSelecionar: (id: string) => void
  aoSelecionarPeca?: (idPeca: string | null) => void
  aoIniciarArrastoParaFora?: (id: string, evento: PointerEvent) => void
  modoPincel?: boolean
}) {
  const { grupoSuspensaoRef, perturbar } = useSuspensaoDoVeiculo(avaliacaoEstabilizacao, suspensaoAtiva)

  useEffect(() => {
    perturbarRef.current = perturbar
    return () => {
      perturbarRef.current = null
    }
  }, [perturbar, perturbarRef])

  return (
    <group ref={grupoSuspensaoRef}>
      <ModeloTabletop
        objeto={objeto}
        corEquipamento={corEquipamento}
        selecionado={selecionado}
        atendimento={atendimento}
        aoSelecionarPeca={aoSelecionarPeca}
        modoPincel={modoPincel}
      />
      {vitimasVinculadas.map((vitima) => (
        <VitimaVinculada3D
          key={vitima.id}
          objeto={vitima}
          veiculo={objeto}
          selecionado={vitima.id === idVitimaSelecionada}
          atendimento={atendimento}
          somenteLeitura={somenteLeitura}
          aoSelecionar={aoSelecionar}
          aoIniciarArrastoParaFora={aoIniciarArrastoParaFora}
        />
      ))}
    </group>
  )
}

function ObjetoEditavel({
  objeto,
  selecionado,
  ferramenta,
  vitimasVinculadas,
  idVitimaSelecionada,
  corEquipamento,
  avaliacaoEstabilizacao,
  pedidoTesteBalanco,
  atendimento,
  somenteLeitura,
  pincel,
  aoSelecionar,
  aoTransformar,
  aoAlterarManipulacao,
  aoIniciarArrastoObjeto,
  aoIniciarArrastoParaFora,
  aoSelecionarPeca,
  cenarioTravado,
  arrastoNaSessao,
  aoRegistrarVeiculo3D,
  tipoParaPosicionar,
  aoPosicionarNovaPeca,
  encaixeAtivo,
  pressaoRef,
}: {
  objeto: ObjetoTabletop
  selecionado: boolean
  ferramenta: FerramentaTabletop
  vitimasVinculadas: readonly ObjetoTabletop[]
  idVitimaSelecionada: string | null
  corEquipamento: EstadoColocacao | undefined
  avaliacaoEstabilizacao: AvaliacaoEstabilizacaoPorVeiculo | undefined
  pedidoTesteBalanco: PedidoTesteBalanco | null
  atendimento?: AtendimentoCena3D
  somenteLeitura?: boolean
  pincel?: PropsCenaTabletop['pincel']
  aoSelecionar: (id: string) => void
  aoTransformar: (id: string, transformacao: TransformacaoTabletop) => void
  aoAlterarManipulacao?: (ativa: boolean) => void
  aoIniciarArrastoObjeto?: (id: string, evento: PointerEvent) => void
  aoIniciarArrastoParaFora?: (id: string, evento: PointerEvent) => void
  aoSelecionarPeca?: (idPeca: string | null) => void
  cenarioTravado?: boolean
  arrastoNaSessao?: boolean
  aoRegistrarVeiculo3D?: (id: string, grupo: THREE.Group | null) => void
  tipoParaPosicionar?: TipoObjetoTabletop | null
  aoPosicionarNovaPeca?: PropsCenaTabletop['aoPosicionarNovaPeca']
  encaixeAtivo?: boolean
  pressaoRef?: MutableRefObject<PontoDeTela | null>
}) {
  const grupo = useRef<Group>(null)
  useEffect(() => {
    if (objeto.veiculo && grupo.current) {
      aoRegistrarVeiculo3D?.(objeto.id, grupo.current)
      return () => aoRegistrarVeiculo3D?.(objeto.id, null)
    }
  }, [objeto.id, objeto.veiculo, aoRegistrarVeiculo3D])
  const posicionando = useContext(ContextoPosicionando)
  const t = objeto.transformacao
  const bloqueado = objeto.bloqueado === true || (cenarioTravado && !ehEquipamentoTabletop(objeto))
  const arrastavel = !bloqueado && (!somenteLeitura || (arrastoNaSessao === true && objeto.tipo !== 'vitima'))
  const modoPincel = Boolean(pincel?.ativo && selecionado && objeto.veiculo && !bloqueado && !somenteLeitura)
  const [previaAmassado, setPreviaAmassado] = useState<{
    ponto: THREE.Vector3
    regiao: string
  } | null>(null)
  const inicioToquePincel = useRef<PontoDeTela | null>(null)
  const perfilPincel = objeto.veiculo ? perfilVeiculoPorId(objeto.veiculo.idPerfil) : null
  const pontoDaSuperficie = (evento: ThreeEvent<PointerEvent | MouseEvent>) => {
    const raiz = grupo.current
    if (!raiz) return null
    raiz.updateWorldMatrix(true, false)
    const local = raiz.worldToLocal(evento.point.clone())
    // O modelo de SUV recebe esta escala interna em ModeloTabletop.
    if (objeto.tipo === 'suv') local.set(local.x, local.y / 1.08, local.z / 1.08)
    return local
  }
  const orientarPrevia = (regiao: string): [number, number, number] =>
    regiao === 'teto' ? [-Math.PI / 2, 0, 0]
      : regiao === 'traseira' ? [0, Math.PI, 0]
        : regiao === 'lateralEsquerda' ? [0, Math.PI / 2, 0]
          : regiao === 'lateralDireita' ? [0, -Math.PI / 2, 0] : [0, 0, 0]

  // suspensão física: só veículos sobre rodas balançam — tombado/capotado/
  // sobre outro veículo já estão apoiados por uma superfície maior, sem o
  // que a suspensão de quatro rodas simula. `perturbarRef` é a ponte para
  // `<ConteudoVeiculoSuspenso>` (só montado para veículos, para não gastar
  // um `useFrame` por objeto não-veículo da cena — árvore, cone, vidro...).
  const suspensaoAtiva = objeto.veiculo?.posicaoFinal === 'sobreRodas'
  const perturbarRef = useRef<((forca: ForcaPerturbacao) => void) | null>(null)

  const selecionar = (evento: ThreeEvent<PointerEvent>) => {
    evento.stopPropagation()
    aoSelecionar(objeto.id)
    // B3: pressionar já candidata a peça para o arraste no chão — soltar sem
    // mover não desloca nada (a posição recalculada cai no mesmo lugar), e
    // continua valendo como seleção simples
    if (arrastavel && ferramenta === 'translate') {
      aoIniciarArrastoObjeto?.(objeto.id, evento.nativeEvent)
    }
    // selecionar NÃO testa o balanço: tocar num carro para ver as
    // propriedades ou focar uma vítima não pode sacudir a cena. O teste
    // vem só de `pedidoTesteBalanco` (botão "Testar estabilidade").
  }

  // o empurrão é aplicado no referencial DESTE carro (via grupo de
  // suspensão), então funciona girado e deslocado igual
  const serialTeste = pedidoTesteBalanco?.serial
  useEffect(() => {
    if (serialTeste === undefined || !suspensaoAtiva) return
    perturbarRef.current?.(FORCA_TESTE_BALANCO)
  }, [serialTeste, suspensaoAtiva])
  const publicarTransformacao = () => {
    const atual = grupo.current
    if (!atual) return
    const escala: [number, number, number] = [
      Math.max(0.05, atual.scale.x),
      Math.max(0.05, atual.scale.y),
      Math.max(0.05, atual.scale.z),
    ]
    atual.scale.set(...escala)
    aoTransformar(objeto.id, {
      posicao: [atual.position.x, atual.position.y, atual.position.z],
      rotacao: [atual.rotation.x, atual.rotation.y, atual.rotation.z],
      escala,
    })
  }

  const triagemDeclarada = atendimento?.triagensDeclaradas[objeto.id]
  const corTriagem = objeto.vitima
    ? (atendimento?.modoExibicao === 'aluno'
        ? (triagemDeclarada ? CORES_TRIAGEM_3D[triagemDeclarada] : '#94a3b8')
        : CORES_TRIAGEM_3D[objeto.vitima.triagem])
    : undefined

  const conteudo = (
    <group
      ref={grupo}
      // Nome estável para a câmera conseguir enquadrar o objeto selecionado.
      name={`${PREFIXO_SELECIONAVEL}${objeto.id}`}
      position={[t.posicao[0], t.posicao[1], t.posicao[2]]}
      rotation={[t.rotacao[0], t.rotacao[1], t.rotacao[2], 'YXZ']}
      scale={[t.escala[0], t.escala[1], t.escala[2]]}
      onPointerDown={modoPincel ? (evento) => {
        evento.stopPropagation()
        inicioToquePincel.current = { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY }
      } : selecionar}
      onPointerMove={modoPincel ? (evento) => {
        const ponto = pontoDaSuperficie(evento)
        const alvo = ponto && perfilPincel ? alvoAmassadoNoPonto(perfilPincel, ponto) : null
        if (!ponto || !alvo) {
          setPreviaAmassado(null)
          return
        }
        setPreviaAmassado((anterior) => anterior?.regiao === alvo.regiao && anterior.ponto.distanceTo(ponto) < 0.03
          ? anterior : { ponto, regiao: alvo.regiao })
      } : undefined}
      onPointerOut={modoPincel ? () => setPreviaAmassado(null) : undefined}
      // Com pincel ligado, clique/tap aplica uma única deformação; movimento
      // antes de soltar não cria amassado nem desloca o veículo.
      onClick={modoPincel ? (evento) => {
        evento.stopPropagation()
        const inicio = inicioToquePincel.current
        inicioToquePincel.current = null
        if (ponteiroArrastou(inicio, { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY })) return
        const ponto = pontoDaSuperficie(evento)
        if (ponto && perfilPincel && alvoAmassadoNoPonto(perfilPincel, ponto)) {
          pincel?.aoAplicar(objeto.id, ponto)
        }
      } : posicionando && tipoParaPosicionar && podeApoiarEmVeiculo(tipoParaPosicionar) && objeto.veiculo ? (evento) => {
        evento.stopPropagation()
        if (ponteiroArrastou(pressaoRef?.current ?? null, { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY })) return
        const p = evento.point
        const [x, z] = encaixeAtivo ? encaixarPosicaoXZ(p.x, p.z) : [p.x, p.z]
        const normal = evento.face
          ? evento.face.normal.clone().transformDirection(evento.object.matrixWorld).normalize()
          : new THREE.Vector3(0, 1, 0)
        const rot = calcularRotacaoSuperficie(tipoParaPosicionar, normal, 0)
        aoPosicionarNovaPeca?.(tipoParaPosicionar, x, z, p.y, rot)
      } : posicionando ? undefined : pararPropagacao}
    >
      {objeto.veiculo ? (
        <ConteudoVeiculoSuspenso
          objeto={objeto}
          corEquipamento={corEquipamento}
          selecionado={selecionado}
          vitimasVinculadas={vitimasVinculadas}
          idVitimaSelecionada={idVitimaSelecionada}
          avaliacaoEstabilizacao={avaliacaoEstabilizacao}
          suspensaoAtiva={suspensaoAtiva}
          perturbarRef={perturbarRef}
          atendimento={atendimento}
          somenteLeitura={somenteLeitura}
          aoSelecionar={aoSelecionar}
          aoSelecionarPeca={aoSelecionarPeca}
          aoIniciarArrastoParaFora={aoIniciarArrastoParaFora}
          modoPincel={modoPincel}
        />
      ) : (
        <ModeloTabletop
          objeto={objeto}
          corEquipamento={corEquipamento}
          selecionado={selecionado}
          atendimento={atendimento}
          aoSelecionarPeca={aoSelecionarPeca}
        />
      )}
      {modoPincel && previaAmassado && (
        <mesh
          raycast={() => null}
          position={objeto.tipo === 'suv'
            ? [previaAmassado.ponto.x, previaAmassado.ponto.y * 1.08, previaAmassado.ponto.z * 1.08]
            : [previaAmassado.ponto.x, previaAmassado.ponto.y, previaAmassado.ponto.z]}
          rotation={orientarPrevia(previaAmassado.regiao)}
        >
          <ringGeometry args={[Math.max(0.12, (pincel?.larguraM ?? 0.65) / 2),
            Math.max(0.12, (pincel?.larguraM ?? 0.65) / 2) + 0.025, 40]} />
          <meshBasicMaterial color="#38bdf8" transparent opacity={0.9} depthTest={false} side={THREE.DoubleSide} />
        </mesh>
      )}
      {objeto.vitima && (
        <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.42, 0.5, 32]} />
          <meshBasicMaterial color={corTriagem} depthTest={false} transparent opacity={0.85} />
        </mesh>
      )}
      {selecionado && (
        <mesh raycast={modoPincel ? () => null : undefined} position={[0, 0.035, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.7, 0.78, 36]} />
          <meshBasicMaterial color="#38bdf8" depthTest={false} transparent opacity={0.9} />
        </mesh>
      )}
    </group>
  )

  // Translação direta pela peça; o gizmo continua disponível para girar/escalar.
  if (!selecionado || bloqueado || somenteLeitura || modoPincel || ferramenta === 'translate') return conteudo

  return (
    <TransformControls
      mode={ferramenta}
      space={ferramenta === 'rotate' ? 'local' : 'world'}
      size={0.82}
      onObjectChange={publicarTransformacao}
      onMouseDown={() => aoAlterarManipulacao?.(true)}
      onMouseUp={() => {
        publicarTransformacao()
        aoAlterarManipulacao?.(false)
      }}
    >
      {conteudo}
    </TransformControls>
  )
}

/**
 * Poça de sangue sob a vítima enquanto a hemorragia da ficha não é controlada
 * — é o que qualquer um vê ao chegar. Menor quando o controle é parcial; some
 * quando o problema é controlado (ver `problemasVisiveisAtivos`).
 */
function Sangramento3D({ intensidade, posicao }: { intensidade: 'ativo' | 'parcial'; posicao: readonly [number, number, number] }) {
  const raio = intensidade === 'ativo' ? 0.75 : 0.4
  return (
    <mesh position={[posicao[0], posicao[1], posicao[2]]} rotation={[-Math.PI / 2, 0, 0]}>
      <circleGeometry args={[raio, 28]} />
      <meshBasicMaterial color="#6b0f0f" transparent opacity={0.92} depthWrite={false} />
    </mesh>
  )
}

/**
 * Vítima vinculada a um assento do veículo: renderizada como filha do grupo
 * do carro (por isso não recebe `position`/`rotation` próprios — herda a
 * rolagem, a guinada e o deslocamento do carro automaticamente), sem gizmo
 * de transformação — mover uma vítima vinculada é trocar de assento pelo
 * painel, não arrastar.
 */
function VitimaVinculada3D({
  objeto,
  veiculo,
  selecionado,
  atendimento,
  somenteLeitura,
  aoSelecionar,
  aoIniciarArrastoParaFora,
}: {
  objeto: ObjetoTabletop
  veiculo: ObjetoTabletop
  selecionado: boolean
  atendimento?: AtendimentoCena3D
  somenteLeitura?: boolean
  aoSelecionar: (id: string) => void
  /** B4 (volta pro chão): pressionar a vítima já sentada candidata a desvincular — só desvincula de fato se o dedo se mover (ver `CenaTabletop`). */
  aoIniciarArrastoParaFora?: (id: string, evento: PointerEvent) => void
}) {
  const posicionando = useContext(ContextoPosicionando)
  const colocacao = colocacaoLocalDaVitimaNoVeiculo(objeto, veiculo)
  if (!colocacao) return null

  const proporcoes = objeto.vitima ? perfilVitimaPorId(objeto.vitima.idPerfil) : ADULTO_MASCULINO
  const triagemDeclarada = atendimento?.triagensDeclaradas[objeto.id]
  const corTriagem = objeto.vitima
    ? (atendimento?.modoExibicao === 'aluno'
        ? (triagemDeclarada ? CORES_TRIAGEM_3D[triagemDeclarada] : '#94a3b8')
        : CORES_TRIAGEM_3D[objeto.vitima.triagem])
    : '#94a3b8'

  const temColar = atendimento
    ? (atendimento.modoExibicao === 'instrutor'
        ? objeto.vitima?.colarCervical
        : (atendimento.colaresColocados[objeto.id] ?? false))
    : objeto.vitima?.colarCervical

  const alturaMarcador =
    proporcoes.alturaPelve + proporcoes.alturaTronco + proporcoes.alturaPescoco + proporcoes.raioCabeca * 2 + 0.12

  return (
    <group
      onClick={posicionando ? undefined : pararPropagacao}
      onPointerDown={(evento) => {
        evento.stopPropagation()
        aoSelecionar(objeto.id)
        if (!somenteLeitura) {
          aoIniciarArrastoParaFora?.(objeto.id, evento.nativeEvent)
        }
      }}
    >
      <VitimaProcedural
        proporcoes={proporcoes}
        colocacao={colocacao}
        colarCervical={temColar}
        regiaoHemorragia={resolverRegiaoHemorragiaVitima(objeto)}
      />
      {atendimento?.sangramentos?.[objeto.id] && (
        <Sangramento3D
          intensidade={atendimento.sangramentos[objeto.id]!}
          // no assoalho, à frente do banco: o sangramento escorre para os pés
          posicao={[colocacao.posicaoPelve.x, Math.max(0.05, colocacao.posicaoPelve.y - 0.42), colocacao.posicaoPelve.z]}
        />
      )}
      {/* Marcador flutuante acima da cabine para seleção e identificação direta da vítima */}
      <group position={[colocacao.posicaoPelve.x, colocacao.posicaoPelve.y + alturaMarcador, colocacao.posicaoPelve.z]}>
        <mesh
          onClick={(e) => {
            e.stopPropagation()
            aoSelecionar(objeto.id)
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
            aoSelecionar(objeto.id)
          }}
        >
          <sphereGeometry args={[0.12, 18, 14]} />
          <meshBasicMaterial color={selecionado ? '#38bdf8' : corTriagem} depthTest={false} />
        </mesh>
        {/* pino de mapa: aro branco e ponta apontando para a vítima */}
        <mesh rotation={[Math.PI / 2, 0, 0]} renderOrder={1}>
          <torusGeometry args={[0.12, 0.022, 8, 28]} />
          <meshBasicMaterial color="#ffffff" depthTest={false} />
        </mesh>
        <mesh position={[0, -0.17, 0]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.085, 0.2, 18]} />
          <meshBasicMaterial color={selecionado ? '#38bdf8' : corTriagem} depthTest={false} />
        </mesh>
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.05, 12, 10]} />
          <meshBasicMaterial color="#ffffff" depthTest={false} transparent opacity={0.95} />
        </mesh>
        {selecionado && (
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.18, 0.23, 24]} />
            <meshBasicMaterial color="#38bdf8" depthTest={false} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    </group>
  )
}

/** Veículo (detalhado ou casca sólida, conforme seleção) com o overlay de vidros trincados por cima. */
function VeiculoComVidros({
  perfil,
  objeto,
  selecionado,
  aoSelecionarPeca,
  modoPincel,
}: {
  perfil: PerfilVeiculo
  objeto: ObjetoTabletop
  selecionado: boolean
  aoSelecionarPeca?: (idPeca: string | null) => void
  modoPincel?: boolean
}) {
  const deformacao = objeto.veiculo ? estadoVisualDeformacoesTabletop(objeto.id, objeto.veiculo) : null

  const temIntervencaoPeca = Boolean(
    objeto.veiculo && (
      (objeto.veiculo.cortes && objeto.veiculo.cortes.length > 0) ||
      Object.values(objeto.veiculo.estadosPecas).some((e) => e !== 'normal')
    ),
  )

  return (
    <>
      {objeto.veiculo && !modoPincel && (selecionado || temIntervencaoPeca) ? (
        <VeiculoDetalhadoTabletop
          perfil={perfil}
          tecnico={selecionado}
          corCarroceria={objeto.cor ?? CORES_AUTOMOTIVAS.alvoCarroceria}
          estadosPecas={objeto.veiculo.estadosPecas}
          pecaSelecionada={selecionado ? objeto.veiculo.pecaSelecionada : null}
          aoSelecionarPeca={selecionado ? aoSelecionarPeca : undefined}
          deformacao={deformacao}
        />
      ) : (
        <VeiculoAlvoCompleto perfil={perfil} corCarroceria={objeto.cor} deformacao={deformacao} />
      )}
      {objeto.veiculo && <VidrosVeiculoOverlay perfil={perfil} vidros={objeto.veiculo.vidros} />}
    </>
  )
}

const IDS_REGIAO_VIDRO: readonly IdVidro[] = [
  'parabrisa',
  'portaDianteiraEsquerda',
  'portaDianteiraDireita',
  'portaTraseiraEsquerda',
  'portaTraseiraDireita',
  'vigiaTraseiro',
]

/**
 * Marca visualmente, sobre a chapa/vidro já existente da carroceria, quais
 * vidros deste carro não estão mais intactos — sem recortar nem substituir a
 * geometria da carroceria (compartilhada com outras abas). Um vidro intacto
 * não desenha nada extra: a casca sólida já dá conta dele.
 */
function VidrosVeiculoOverlay({
  perfil,
  vidros,
}: {
  perfil: PerfilVeiculo
  vidros: Readonly<Partial<Record<IdVidro, EstadoManejoVidro>>>
}) {
  const afetados = IDS_REGIAO_VIDRO.filter((id) => {
    const estado = vidros[id]
    return estado !== undefined && estado !== 'intacto'
  })
  if (afetados.length === 0) return null

  const regioes = regioesVidroDoPerfil(perfil)
  return (
    <>
      {afetados.map((id) => {
        const reg = regioes[id]
        return (
          <group key={id} position={[...reg.posicao]} rotation={[...reg.rotacao]}>
            <VidroDanificado
              estado={vidros[id] as Exclude<EstadoManejoVidro, 'intacto'>}
              largura={reg.dimensoes[0]}
              altura={reg.dimensoes[1]}
              semente={IDS_REGIAO_VIDRO.indexOf(id) * 97 + 13}
            />
          </group>
        )
      })}
    </>
  )
}

function ModeloTabletop({
  objeto,
  corEquipamento,
  selecionado,
  atendimento,
  aoSelecionarPeca,
  modoPincel,
}: {
  objeto: ObjetoTabletop
  corEquipamento?: EstadoColocacao
  selecionado: boolean
  atendimento?: AtendimentoCena3D
  aoSelecionarPeca?: (idPeca: string | null) => void
  modoPincel?: boolean
}) {
  switch (objeto.tipo) {
    case 'carro': {
      const perfil = objeto.veiculo ? perfilVeiculoPorId(objeto.veiculo.idPerfil) : HATCH_COMPACTO
      return <VeiculoComVidros perfil={perfil} objeto={objeto} selecionado={selecionado} aoSelecionarPeca={aoSelecionarPeca} modoPincel={modoPincel} />
    }
    case 'suv': {
      const perfil = objeto.veiculo ? perfilVeiculoPorId(objeto.veiculo.idPerfil) : SEDAN_MEDIO
      return (
        <group scale={[1, 1.08, 1.08]}>
          <VeiculoComVidros perfil={perfil} objeto={objeto} selecionado={selecionado} aoSelecionarPeca={aoSelecionarPeca} modoPincel={modoPincel} />
        </group>
      )
    }
    case 'caminhao':
      return <CaminhaoTabletop cor={objeto.cor ?? '#e2e8f0'} />
    case 'onibus':
      return <OnibusTabletop cor={objeto.cor} />
    case 'resgate':
      return <ResgateTabletop cor={objeto.cor} />
    case 'caminhaoBombeiros':
      return <CaminhaoBombeirosTabletop />
    case 'bombeiro':
      return <BombeiroTabletop />
    case 'bombeiroMedico':
      return <BombeiroMedicoTabletop />
    case 'areaUsa':
      return <AreaUsaTabletop />
    case 'moto':
      return <MotoTabletop cor={objeto.cor ?? '#dc2626'} />
    case 'arvore':
      return <ArvoreTabletop />
    case 'poste':
      return <PosteTabletop />
    case 'defensa':
      return <DefensaTabletop />
    case 'cone':
      return <ConeTabletop />
    case 'bloco':
      return <BlocoCribbing3D cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : '#d4a35d'} />
    case 'vitima': {
      const proporcoes = objeto.vitima ? perfilVitimaPorId(objeto.vitima.idPerfil) : ADULTO_MASCULINO
      const postura = (objeto.vitima?.local.tipo === 'solo' ? objeto.vitima.local.postura : 'sentada') as PosturaVitimaSolo

      let yPelve = 0.45
      let reclinacaoRad = 0
      let rolagemRad = 0
      let pose = POSE_SENTADA

      switch (postura) {
        case 'emPe':
          yPelve = 0.92
          reclinacaoRad = 0
          rolagemRad = 0
          pose = POSE_ALINHADA
          break
        case 'sentada':
          yPelve = 0.45
          reclinacaoRad = 0
          rolagemRad = 0
          pose = POSE_SENTADA
          break
        case 'decubitoDorsal':
          yPelve = 0.12
          reclinacaoRad = Math.PI / 2
          rolagemRad = 0
          pose = POSE_ALINHADA
          break
        case 'decubitoVentral':
          yPelve = 0.12
          reclinacaoRad = -Math.PI / 2
          rolagemRad = 0
          pose = POSE_ALINHADA
          break
        case 'decubitoLateral':
          yPelve = 0.14
          reclinacaoRad = Math.PI / 2
          rolagemRad = Math.PI / 2
          pose = POSE_ALINHADA
          break
      }

      const temColar = atendimento
        ? (atendimento.modoExibicao === 'instrutor'
            ? objeto.vitima?.colarCervical
            : (atendimento.colaresColocados[objeto.id] ?? false))
        : objeto.vitima?.colarCervical

      return (
        <VitimaProcedural
          proporcoes={proporcoes}
          colocacao={{
            posicaoPelve: { x: 0, y: yPelve, z: 0 },
            guinadaRad: 0,
            reclinacaoRad,
            rolagemRad,
          }}
          pose={pose}
          colarCervical={temColar}
          regiaoHemorragia={resolverRegiaoHemorragiaVitima(objeto)}
        />
      )
    }
    case 'vidro':
      return <VidroTabletop />
    case 'detritosVidro': {
      const aparencia = atendimento?.riscos?.[objeto.id]
      // varrido na sessão: sai da cena (o cenário salvo continua com ele)
      if (aparencia?.fragmentosRemovidos) return null
      return (
        <>
          <DetritosVidroPersistentes3D tipo="estilhacosTemperado" local="solo" posicaoBase={[0, 0, 0]} />
          {aparencia?.coberto && <CobertorTabletop />}
          <AreaDeToque largura={0.9} profundidade={0.9} />
        </>
      )
    }
    case 'derramamento':
      return <DerramamentoTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'caboEletrico':
      return <CaboEletricoTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'manchaSangue':
      return <ManchaSangueTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'fogo':
      return <FogoTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'extintorPQS':
      return <ExtintorPqsTabletop />
    case 'lonaFerramentas':
      return <LonaFerramentasTabletop />
    case 'mochilaTrauma':
      return <MochilaTraumaTabletop />
    case 'calco':
      return (
        <StepChock3D
          cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : '#1c2024'}
          comCunhaAjuste={objeto.equipamento?.comCunhaAjuste}
        />
      )
    case 'cunha':
      return <CunhaResgate3D cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : '#1c2024'} />
    case 'vstrut':
      return (
        <VStrutTabletop
          cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : undefined}
          alcance={objeto.equipamento?.alcance}
        />
      )
    case 'fitaCatraca':
      return <FitaCatracaTabletop cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : undefined} />
    case 'cobertor':
      return <CobertorTabletop />
    case 'lagrima':
      return <LagrimaTabletop />
    case 'prancha':
      return <PranchaTabletop />
    case 'muro':
      return <MuroTabletop />
  }
}

/**
 * Veículo selecionado: estrutura completa (gaiola, portas, SRS, cintos),
 * cada um com seu próprio contexto de peças — assim vários carros detalhados
 * na mesma cena não compartilham estado de peça entre si.
 */
function VeiculoDetalhadoTabletop({
  perfil,
  tecnico,
  corCarroceria,
  estadosPecas,
  pecaSelecionada,
  aoSelecionarPeca,
  deformacao,
}: {
  perfil: PerfilVeiculo
  /** selecionado para editar peças: estrutura em raio-X */
  tecnico: boolean
  corCarroceria?: string
  estadosPecas: MapaEstados
  pecaSelecionada?: string | null
  aoSelecionarPeca?: (idPeca: string | null) => void
  deformacao?: EstadoDeformacaoVeiculo | null
}) {
  const colunasSeccionadas = useMemo(() => {
    const lista: string[] = []
    for (const [id, estado] of Object.entries(estadosPecas)) {
      if (id.startsWith('coluna') && (estado === 'removida' || estado === 'afastada')) {
        if (id.includes('colunaA') && !lista.includes('colunaA')) lista.push('colunaA')
        if (id.includes('colunaB') && !lista.includes('colunaB')) lista.push('colunaB')
        if (id.includes('colunaC') && !lista.includes('colunaC')) lista.push('colunaC')
      }
    }
    return lista
  }, [estadosPecas])

  const valor: ValorMaquete = useMemo(
    () => ({
      perfil,
      modo: 'estrutura',
      camadas: tecnico ? CAMADAS_ESTRUTURA_TABLETOP : CAMADAS_VEICULO_REAL_TABLETOP,
      opacidadeCarroceria: tecnico ? OPACIDADE_CARROCERIA_TABLETOP : 1,
      corCarroceria: tecnico ? undefined : corCarroceria,
      estados: estadosPecas,
      selecionada: pecaSelecionada ?? null,
      selecionar: aoSelecionarPeca ?? NOOP_SELECIONAR,
      mostrarRotulos: false,
      expansaoAirbag: 0,
      srsAcionados: LISTA_VAZIA,
      retencaoEmEvidencia: false,
      idsEmAlerta: LISTA_VAZIA,
      colunasSeccionadas,
      semCintoAncoras: LISTA_VAZIA,
      // no Table top a aula é sobre posição e estabilização, não sobre aço
      pinturaPorAco: false,
    }),
    [perfil, tecnico, corCarroceria, estadosPecas, pecaSelecionada, aoSelecionarPeca, colunasSeccionadas],
  )

  return (
    <ProvedorMaquete valor={valor}>
      <VeiculoDidatico perfil={perfil} deformacao={deformacao} />
    </ProvedorMaquete>
  )
}

function Material({ cor }: { cor: string }) {
  return <meshStandardMaterial color={cor} roughness={0.62} metalness={0.12} />
}

function CaminhaoTabletop({ cor }: { cor: string }) {
  return <CaminhaoCarga3D cor={cor} />
}

function MotoTabletop({ cor }: { cor: string }) {
  return <Moto3D cor={cor} />
}

function ArvoreTabletop() {
  return <ArvoreMaquete />
}

function DefensaTabletop() {
  return <DefensaMaquete />
}

function ConeTabletop() {
  return (
    <group>
      <mesh position={[0, 0.04, 0]}>
        <CaixaArredondada args={[0.55, 0.08, 0.55]} />
        <meshStandardMaterial color="#24272a" />
      </mesh>
      <mesh position={[0, 0.42, 0]}>
        <coneGeometry args={[0.24, 0.76, 18]} />
        <meshStandardMaterial color="#f97316" roughness={0.7} />
      </mesh>
    </group>
  )
}

/**
 * Sem `transmission` de propósito: com ela o three redesenha a cena opaca inteira
 * num buffer extra por quadro enquanto o vidro estiver visível. Com `opacity` de
 * 0,62 a refração quase não se vê, e a mistura por transparência basta.
 */
function VidroTabletop() {
  return (
    <group position={[0, 0.48, 0]}>
      <mesh>
        <boxGeometry args={[1.25, 0.82, 0.025]} />
        <meshPhysicalMaterial
          color="#bae6fd"
          roughness={0.06}
          metalness={0.2}
          transparent
          opacity={0.62}
          depthWrite={false}
        />
        <Edges color="#38bdf8" />
      </mesh>
      <mesh position={[0, 0, 0.016]}>
        <planeGeometry args={[1.18, 0.75, 6, 4]} />
        <meshBasicMaterial color="#ffffff" wireframe transparent opacity={0.45} />
      </mesh>
    </group>
  )
}

function VStrutTabletop({
  cor,
  alcance = [0.55, 1.15, 0],
}: {
  cor?: string
  alcance?: readonly [number, number, number]
}) {
  return (
    <VStrut3D
      para={[alcance[0], alcance[1], alcance[2]]}
      cor={cor}
      escala={0.92}
    />
  )
}

function FitaCatracaTabletop({ cor = '#ea580c' }: { cor?: string }) {
  return (
    <group>
      {/* Mecanismo tensor da catraca de alta tonelagem */}
      <group position={[0, 0.12, 0]}>
        {/* Corpo do chassi de aço zincado */}
        <mesh position={[0, 0, 0]} castShadow>
          <boxGeometry args={[0.22, 0.14, 0.22]} />
          <meshStandardMaterial color="#64748b" metalness={0.7} roughness={0.3} />
          <Edges color="#334155" />
        </mesh>
        {/* Tambor central com rolo de fita recolhida */}
        <mesh position={[0, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.045, 0.045, 0.18, 16]} />
          <meshStandardMaterial color={cor} roughness={0.6} />
        </mesh>
        {/* Alavanca de acionamento ergonômica amarela */}
        <group position={[0.08, 0.04, 0]} rotation={[0, 0, -0.45]}>
          <mesh position={[0, 0.08, 0]}>
            <CaixaArredondada args={[0.025, 0.16, 0.16]} />
            <meshStandardMaterial color="#475569" metalness={0.8} roughness={0.3} />
          </mesh>
          <mesh position={[0, 0.17, 0]}>
            <CaixaArredondada args={[0.035, 0.06, 0.18]} />
            <meshStandardMaterial color="#facc15" roughness={0.4} />
          </mesh>
        </group>
      </group>

      {/* Cinta/fita de poliéster de 50mm de amarração monobloco */}
      {/* Tramo ascendente ancorando na coluna/chassi do carro superior */}
      <mesh position={[0, 0.65, 0]} castShadow>
        <boxGeometry args={[0.015, 0.9, 0.12]} />
        <meshStandardMaterial color={cor} roughness={0.6} />
        <Edges color="#c2410c" />
      </mesh>
      {/* Gancho de aço forjado no topo da cinta */}
      <mesh position={[0, 1.1, 0]}>
        <torusGeometry args={[0.045, 0.016, 8, 16, Math.PI * 1.4]} />
        <meshStandardMaterial color="#334155" metalness={0.85} roughness={0.25} />
      </mesh>
      {/* Tramo descendente ancorando no veículo inferior / chassi */}
      <mesh position={[0, 0.02, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
        <boxGeometry args={[0.015, 0.6, 0.12]} />
        <meshStandardMaterial color={cor} roughness={0.6} />
        <Edges color="#c2410c" />
      </mesh>
    </group>
  )
}

function CobertorTabletop() {
  return (
    <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[1.1, 0.75, 6, 6]} />
      <meshStandardMaterial color="#94a3b8" roughness={0.85} side={2} />
    </mesh>
  )
}

const CORES_ABSORVENTE: Record<MaterialAbsorvente, string> = {
  serragem: '#b8864a',
  areia: '#d8c38e',
  absorvente_industrial: '#e5e7eb',
}

/**
 * Área invisível que recebe o toque de objetos finos ou pulverizados
 * (fragmentos de vidro, cabo caído): sem ela, acertar a peça com o dedo
 * dependeria de tocar exatamente numa partícula de poucos milímetros.
 */
function AreaDeToque({
  largura,
  profundidade,
  centro = [0, 0, 0],
}: {
  largura: number
  profundidade: number
  centro?: readonly [number, number, number]
}) {
  return (
    <mesh position={[centro[0], 0.05, centro[2]]}>
      <CaixaArredondada args={[largura, 0.1, profundidade]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  )
}

/**
 * Faixa zebrada de isolamento no chão (amarelo e preto), desenhada em
 * segmentos de anel — aparece quando a equipe isola a área de um risco.
 */
function IsolamentoArea3D({ raio }: { raio: number }) {
  const segmentos = 24
  const passo = (Math.PI * 2) / segmentos
  return (
    <group position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      {Array.from({ length: segmentos }, (_, i) => (
        <mesh key={i}>
          <ringGeometry args={[raio - 0.07, raio, 4, 1, i * passo, passo]} />
          <meshBasicMaterial color={i % 2 === 0 ? '#facc15' : '#111827'} side={2} />
        </mesh>
      ))}
    </group>
  )
}

/**
 * Mancha de óleo/combustível: escura e brilhante; maior enquanto o
 * vazamento continua; com a camada de serragem/areia/absorvente por cima
 * quando a equipe aplica.
 */
function DerramamentoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const raio = aparencia?.vazando === false ? 0.85 : 1.1
  return (
    <group>
      {/* Base escura do óleo/combustível no piso com brilho especular */}
      <mesh position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.75, 1]}>
        <circleGeometry args={[raio, 48]} />
        <meshStandardMaterial color="#1c1917" roughness={0.06} metalness={0.4} />
      </mesh>

      {/* Camada líquida âmbar translúcida característica da gasolina/combustível */}
      <mesh position={[0, 0.014, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[0.96, 0.72, 1]}>
        <circleGeometry args={[raio * 0.94, 40]} />
        <meshStandardMaterial
          color="#b45309"
          roughness={0.12}
          metalness={0.3}
          transparent
          opacity={0.65}
          depthWrite={false}
        />
      </mesh>

      {/* Película de arco-íris iridescente (shimmer de hidrocarboneto sobre asfalto) */}
      <mesh position={[0, 0.016, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[0.88, 0.66, 1]}>
        <ringGeometry args={[raio * 0.35, raio * 0.82, 36]} />
        <meshStandardMaterial
          color="#06b6d4"
          emissive="#0284c7"
          emissiveIntensity={0.35}
          roughness={0.08}
          metalness={0.7}
          transparent
          opacity={0.48}
          depthWrite={false}
        />
      </mesh>

      {/* Borda / menisco dourado nítido para delimitar a poça contra o chão escuro */}
      <mesh position={[0, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.75, 1]}>
        <ringGeometry args={[raio * 0.94, raio * 1.02, 48]} />
        <meshStandardMaterial
          color="#f59e0b"
          emissive="#d97706"
          emissiveIntensity={0.5}
          roughness={0.2}
          metalness={0.3}
          transparent
          opacity={0.85}
          depthWrite={false}
        />
      </mesh>

      {/* Camada de absorvente sobreposta quando aplicada pela guarnição */}
      {aparencia?.absorvente && (
        <mesh position={[0, 0.022, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.75, 1]}>
          <circleGeometry args={[0.86, 32]} />
          <meshStandardMaterial color={CORES_ABSORVENTE[aparencia.absorvente]} roughness={1} />
        </mesh>
      )}

      {/* Indicador de risco inflamável ativo */}
      {aparencia?.vazando !== false && (
        <group position={[0, 0.28, 0]}>
          <mesh rotation={[0, 0, Math.PI / 4]}>
            <planeGeometry args={[0.2, 0.2]} />
            <meshBasicMaterial color="#ef4444" side={THREE.DoubleSide} />
          </mesh>
          <mesh position={[0, 0, 0.005]} rotation={[0, 0, Math.PI / 4]}>
            <planeGeometry args={[0.15, 0.15]} />
            <meshBasicMaterial color="#fbbf24" side={THREE.DoubleSide} />
          </mesh>
        </group>
      )}

      {aparencia?.areaIsolada && <IsolamentoArea3D raio={1.7} />}
      <AreaDeToque largura={raio * 2.2} profundidade={raio * 1.6} />
    </group>
  )
}

/**
 * Cabo caído no chão, com uma faísca na ponta enquanto a rede estiver
 * energizada — só a confirmação de desligamento pela concessionária apaga.
 */
function CaboEletricoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const energizado = aparencia?.energizado ?? true
  const curva = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(0, 0.03, 0),
        new THREE.Vector3(0.8, 0.03, 0.35),
        new THREE.Vector3(1.7, 0.03, -0.1),
        new THREE.Vector3(2.6, 0.03, 0.25),
      ]),
    [],
  )
  return (
    <group>
      <mesh>
        <tubeGeometry args={[curva, 32, 0.025, 8, false]} />
        <meshStandardMaterial color={energizado ? '#1f2937' : '#6b7280'} roughness={0.6} />
      </mesh>
      {/* Faíscas de choque elétrico na ponta energizada enquanto o circuito estiver ligado */}
      <FaiscasChoqueEletrico3D posicao={[2.6, 0, 0.25]} escala={1.15} ativo={energizado} />
      {aparencia?.areaIsolada && (
        <group position={[1.3, 0, 0.1]}>
          <IsolamentoArea3D raio={2.4} />
        </group>
      )}
      <AreaDeToque largura={2.9} profundidade={0.8} centro={[1.3, 0, 0.1]} />
    </group>
  )
}

/**
 * Foco de fogo / princípio de incêndio na cena tática.
 * Pode ser posicionado no veículo (motor, capô, habitáculo) ou em qualquer ponto da via.
 */
function FogoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const extinto = aparencia?.extinto ?? false
  return (
    <group>
      <Fogo3D ativo={!extinto} escala={1.25} />
      {aparencia?.areaIsolada && (
        <group position={[0, 0, 0]}>
          <IsolamentoArea3D raio={2.2} />
        </group>
      )}
      <AreaDeToque largura={1.4} profundidade={1.4} centro={[0, 0, 0]} />
    </group>
  )
}

function LagrimaTabletop() {
  return (
    <mesh position={[0, 0.4, 0]}>
      <boxGeometry args={[0.6, 0.72, 0.04]} />
      <meshStandardMaterial color="#e2e8f0" transparent opacity={0.7} roughness={0.2} />
      <Edges color="#94a3b8" />
    </mesh>
  )
}

function PranchaTabletop() {
  return (
    <mesh position={[0, 0.1, 0]}>
      <boxGeometry args={[0.45, 0.05, 1.85]} />
      <meshStandardMaterial color="#f97316" roughness={0.6} />
      <Edges color="#7c2d12" />
    </mesh>
  )
}

function MuroTabletop() {
  return (
    <group>
      {/* Sapata de base larga tipo New Jersey assentada no solo */}
      <mesh position={[0, 0.12, 0]}>
        <CaixaArredondada args={[3.8, 0.24, 0.54]} />
        <meshStandardMaterial color="#475569" roughness={0.95} />
      </mesh>
      {/* Bloco de concreto rígido transversal ao impacto (largura em X, espessura em Z) */}
      <mesh position={[0, 0.88, 0]}>
        <boxGeometry args={[3.6, 1.36, 0.36]} />
        <meshStandardMaterial color="#64748b" roughness={0.92} />
        <Edges color="#334155" />
      </mesh>
      {/* Topo chanfrado de proteção */}
      <mesh position={[0, 1.58, 0]}>
        <CaixaArredondada args={[3.68, 0.08, 0.4]} />
        <meshStandardMaterial color="#94a3b8" roughness={0.85} />
      </mesh>
      {/* juntas entre as placas de concreto, nas duas faces */}
      {[-1.2, 0, 1.2].flatMap((x) =>
        [-1, 1].map((f) => (
          <mesh key={`${x}:${f}`} position={[x, 0.88, f * 0.181]}>
            <boxGeometry args={[0.025, 1.36, 0.004]} />
            <meshStandardMaterial color="#334155" roughness={1} />
          </mesh>
        )),
      )}
      {/* Faixa reflexiva amarela de tráfego/alerta na face frontal de impacto (-Z voltada para o veículo) */}
      <mesh position={[0, 0.88, -0.182]}>
        <planeGeometry args={[3.4, 0.2]} />
        <meshStandardMaterial color="#facc15" roughness={0.4} />
      </mesh>
    </group>
  )
}

